import { Component, Input } from '@angular/core';

let _twUid = 0;

/**
 * The Tokyo Tower glyph, shared between the live asset (line-menu-button's Home
 * tab) and its preview (line-home's tab bar) so the two can never drift — a
 * single source of truth for the silhouette, the lattice truss, the observation
 * decks and the deck lights. Owner 2026-08-05: "ortak component yapmazsan
 * uğraşırsın böyle her seferinde."
 *
 * ON = a filled body in `color` with a lit-up lattice + glowing deck lamps;
 * OFF = the same body, dimmer, with no lamps. The caller owns the box size
 * (via the host element's width/height) and the colour.
 */
@Component( {
  selector: 'app-line-tower-glyph',
  templateUrl: './line-tower-glyph.component.html',
  styleUrls: [ './line-tower-glyph.component.scss' ],
} )
export class LineTowerGlyphComponent {

  /** Body fill (the tower colour). */
  @Input() color = '#DEA8F7';

  /** Lit (active tab) vs idle. Lit = glowing deck lamps. */
  @Input() on = false;

  /** Per-instance id prefix so multiple towers on one page keep distinct
   *  clipPath / filter references. */
  readonly uid = 'tw' + ( _twUid++ );

  /** The tower silhouette — flared concave legs, a flat apex, decks that jut. */
  readonly towerGlyph =
    'M50 6 L53 21 L61 33 L55 44 L62 54 Q65 72 88 92 L67 92 Q62 79 52 76 L48 76 Q38 79 33 92 L12 92 Q35 72 38 54 L45 44 L39 33 L47 21 Z';

  /** The two observation decks (main = lower/wider, special = upper). */
  readonly towerDecks = [ { x: 31, y: 52, w: 38, h: 4 }, { x: 36, y: 30.5, w: 28, h: 3.4 } ];

  /** A 4-point twinkle (concave sparkle), unit-sized, placed via a transform. */
  readonly sparklePath =
    'M0,-1 Q0.16,-0.16 1,0 Q0.16,0.16 0,1 Q-0.16,0.16 -1,0 Q-0.16,-0.16 0,-1 Z';

  /** A few glowing stars scattered in the negative space around the tower —
   *  only shown when lit (owner 2026-08-05). */
  readonly stars = [
    { x: 17, y: 20, s: 5.2 }, { x: 83, y: 18, s: 5.8 }, { x: 86, y: 52, s: 4.2 },
  ];

  /** Warm lamps along the decks — lit only when `on`. */
  readonly towerLights = [
    { cx: 36, cy: 54, r: 1.4 }, { cx: 43, cy: 54, r: 1.4 }, { cx: 50, cy: 54, r: 1.4 },
    { cx: 57, cy: 54, r: 1.4 }, { cx: 64, cy: 54, r: 1.4 },
    { cx: 42, cy: 32.2, r: 1.3 }, { cx: 50, cy: 32.2, r: 1.3 }, { cx: 58, cy: 32.2, r: 1.3 },
  ];

  /** Symmetric criss-cross truss + horizontal chords, clipped to the body. */
  get towerMesh(): string[] {
    const lines: string[] = [];
    const run = 22;
    for ( let k = -42; k <= 42; k += 12 ) {
      const bx = 50 + k;
      lines.push( `M${ bx } 93 L${ bx + run } 6` );
      lines.push( `M${ 100 - bx } 93 L${ 100 - bx - run } 6` );
    }
    for ( let y = 16; y <= 90; y += 10 ) { lines.push( `M8 ${ y } L92 ${ y }` ); }
    return lines;
  }

  /** A darker shade of `color` for the lattice lines (the light violet body
   *  needs a darker mesh to read). */
  get towerMeshColor(): string {
    const h = ( this.color || '#000000' ).replace( '#', '' );
    const full = h.length === 3 ? h.split( '' ).map( c => c + c ).join( '' ) : h;
    const c = [ 0, 2, 4 ].map( i => Math.round( ( parseInt( full.substr( i, 2 ), 16 ) || 0 ) * 0.6 ) );
    return '#' + c.map( v => v.toString( 16 ).padStart( 2, '0' ) ).join( '' );
  }
}
